// Complete search ordering using native presentment prices; non-products retain their native page slots.
const PRICE_SORTS = new Set(['price-ascending', 'price-descending']);
const IDENTITY_KEYS = ['shop', 'theme', 'role', 'query', 'types', 'path', 'country', 'market', 'currency', 'locale', 'signed_in', 'price_strategy'];
const MAX_PRODUCTS = 1000;
const MAX_SOURCE_PAGES = 12;

export class PriceSortError extends Error {
  constructor(code) { super(code); this.name = 'PriceSortError'; this.code = code; }
}
const fail = code => { throw new PriceSortError(code); };
const integer = (value, min = 0) => Number.isSafeInteger(value) && value >= min;
export const isPriceSort = sort => PRICE_SORTS.has(sort);
const rootOf = node => node?.matches?.('raith-search-results') ? node : node?.querySelector?.('raith-search-results');
const directContext = root => [...(root?.children || [])].filter(node => node.matches('script[data-search-price-context]'));
const filterKey = filters => JSON.stringify(filters.map(pair => [...pair]).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))));

function validateContext(context, mode) {
  if (!context || context.version !== 1 || context.mode !== mode || context.shop !== 'raithclothing.myshopify.com' || typeof context.theme !== 'string' || !/^[1-9]\d*$/.test(context.theme) || !['main', 'unpublished'].includes(context.role)) fail('context');
  for (const key of IDENTITY_KEYS) {
    if (key === 'signed_in') { if (typeof context[key] !== 'boolean') fail('context'); }
    else if (typeof context[key] !== 'string' || (!context[key] && key !== 'price_strategy')) fail('context');
  }
  if (!isPriceSort(context.sort) || !integer(context.total) || context.total > MAX_PRODUCTS || !integer(context.page, 1) || !integer(context.pages) || !integer(context.page_size, 1) || context.page_size > 50 || !integer(context.offset)) fail('pagination');
  const pages = Math.ceil(context.total / context.page_size);
  if (context.pages !== pages && !(context.total === 0 && context.pages === 1)) fail('pagination');
  if (context.page > Math.max(1, pages) || context.offset !== (context.page - 1) * context.page_size) fail('pagination');
  if (!Array.isArray(context.filters) || context.filters.some(pair => !Array.isArray(pair) || pair.length !== 2 || !pair.every(value => typeof value === 'string') || !pair[0].startsWith('filter.'))) fail('filters');
  if (!Array.isArray(context.items) || context.items.length !== Math.min(context.page_size, Math.max(0, context.total - context.offset))) fail('incomplete');
  const ids = new Set();
  for (const item of context.items) {
    if (!Array.isArray(item) || item.length !== 2 || typeof item[0] !== 'string' || !/^(product|article|page):[1-9]\d*$/.test(item[0]) || (item[0].startsWith('product:') ? !integer(item[1]) : item[1] !== null) || ids.has(item[0])) fail('items');
    ids.add(item[0]);
  }
  return context;
}

function contextFromRoot(root, requirePrice = true) {
  const scripts = directContext(root);
  if (scripts.length !== 1) fail('context');
  let context;
  try { context = JSON.parse(scripts[0].textContent); } catch { fail('context'); }
  if (!requirePrice) return context;
  return validateContext(context, 'native');
}

function sameIdentity(left, right) {
  if (!right || IDENTITY_KEYS.some(key => left[key] !== right[key])) fail('context-changed');
}

function sameResult(left, right) {
  sameIdentity(left, right);
  if (left.sort !== right.sort || left.total !== right.total || filterKey(left.filters) !== filterKey(right.filters)) fail('results-changed');
}

function nativeSource(section) {
  const root = rootOf(section), context = contextFromRoot(root);
  const expected = context.items.filter(([key]) => key.startsWith('product:'));
  const streams = [...root.querySelectorAll('[data-search-product-list]')];
  if (streams.length !== (expected.length ? 1 : 0)) fail('cards');
  const cards = streams.length ? [...streams[0].children] : [];
  if (cards.length !== expected.length || cards.some((card, index) => `product:${card.dataset.searchProductId}` !== expected[index][0])) fail('cards');
  return { root, context, cards, stream: streams[0] };
}

// Parsing in template.content keeps discarded images, galleries and custom elements inert.
function inertSection(html, document, sectionId) {
  const template = document.createElement('template');
  template.innerHTML = html;
  const matches = [...template.content.querySelectorAll('[id]')].filter(node => node.id === `shopify-section-${sectionId}`);
  if (matches.length !== 1) fail('section');
  return matches[0];
}

async function pool(values, work, check) {
  let next = 0;
  await Promise.all(Array.from({ length: Math.min(2, values.length) }, async () => {
    while (next < values.length) {
      check();
      const value = values[next++];
      await work(value);
      check();
    }
  }));
}

export async function preparePriceSortedSection(section, { url, signal, isCurrent = () => true, expectedRoot, win = window, fetch: fetchOverride } = {}) {
  const source = nativeSource(section), { root, context } = source;
  const target = new URL(url, win.location.href);
  if (target.origin !== win.location.origin || target.pathname !== context.path || !root.dataset.priceSortModule) fail('route');
  const sort = target.searchParams.get('sort_by') || context.sort;
  const requestedPage = target.searchParams.has('page') ? Number(target.searchParams.get('page')) : 1;
  if (sort !== context.sort || requestedPage !== context.page || target.searchParams.get('q')?.trim() !== context.query.trim()) fail('route');
  if (expectedRoot) sameIdentity(context, contextFromRoot(expectedRoot, false));
  const sectionId = root.dataset.sectionId;
  if (!sectionId || (target.searchParams.has('section_id') && target.searchParams.get('section_id') !== sectionId)) fail('section');

  const controller = new win.AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  const timeout = win.setTimeout(abort, 20000);
  const check = () => {
    if (signal?.aborted || controller.signal.aborted || !isCurrent()) fail('stale-or-timeout');
  };
  const fetcher = fetchOverride || win.fetch.bind(win);
  const read = async (request, maxLength) => {
    check();
    const response = await fetcher(request.toString(), { signal: controller.signal, credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' } });
    check();
    if (!response.ok) fail('request');
    if (response.url) {
      const returned = new URL(response.url, request);
      if (returned.origin !== request.origin || returned.pathname !== request.pathname) fail('redirect');
    }
    const text = await response.text();
    check();
    if (text.length > maxLength) fail('response-size');
    return text;
  };

  try {
    check();
    // Shopify moves nonproducts to the end of each returned search page.
    // Changing page size for a separate index changes those page boundaries.
    // Read the actual native section pages and sort only their product slots.
    if (context.pages > MAX_SOURCE_PAGES) fail('source-limit');
    const sources = new Map([[context.page, source]]);
    await pool(Array.from({length: context.pages}, (_, index) => index + 1).filter(page => page !== context.page), async page => {
      const request = new URL(target);
      request.searchParams.set('section_id', sectionId); request.searchParams.delete('sections');
      if (page === 1) request.searchParams.delete('page'); else request.searchParams.set('page', String(page));
      const candidate = nativeSource(inertSection(await read(request, 4000000), win.document, sectionId));
      sameResult(context, candidate.context);
      if (candidate.context.page !== page || candidate.root.dataset.sectionId !== sectionId) fail('source-page');
      if (candidate.context.page_size !== context.page_size) fail('page-size');
      sources.set(page, candidate);
    }, check);
    const items = [...sources.entries()].sort(([a], [b]) => a - b).flatMap(([, candidate]) => candidate.context.items);
    if (items.length !== context.total || new Set(items.map(([id]) => id)).size !== items.length) fail('incomplete-results');
    const productOffset = items.slice(0, context.offset).filter(([id]) => id.startsWith('product:')).length;
    const productCount = context.items.filter(([id]) => id.startsWith('product:')).length;
    const direction = sort === 'price-descending' ? -1 : 1;
    const ranked = items.map(([id, price], ordinal) => ({id, price, ordinal})).filter(item => item.id.startsWith('product:'));
    ranked.sort((a, b) => direction * (a.price - b.price) || a.ordinal - b.ordinal);
    const selected = ranked.slice(productOffset, productOffset + productCount);
    check();

    const cards = selected.map(item => {
      const page = Math.floor(item.ordinal / context.page_size) + 1;
      const candidate = sources.get(page);
      const card = candidate.cards.find(card => `product:${card.dataset.searchProductId}` === item.id);
      if (!card) fail('selected-card');
      return card.cloneNode(true);
    });
    if (source.stream) source.stream.replaceChildren(...cards);
    for (const card of cards) for (const image of card.querySelectorAll('img')) {
      image.setAttribute('loading', 'lazy'); image.setAttribute('fetchpriority', 'auto');
    }
    const lead = cards[0]?.querySelector('img');
    if (lead) { lead.setAttribute('loading', 'eager'); lead.setAttribute('fetchpriority', 'high'); }
    root.dataset.priceSortState = 'ready';
    root.querySelector('[data-price-sort-status]')?.setAttribute('hidden', '');
    return section;
  } finally {
    win.clearTimeout(timeout);
    signal?.removeEventListener('abort', abort);
    controller.abort();
  }
}

export function registerRaithSearchResults(win = window) {
  if (win.customElements.get('raith-search-results')) return;
  class RaithSearchResults extends win.HTMLElement {
    connectedCallback() {
      if (!this.dataset.priceSortModule) return;
      this.setPriceSortStatus(this.dataset.priceSortState || 'native');
      if (this.dataset.priceSortState === 'pending') this.initializePriceSort();
    }
    disconnectedCallback() { this.priceSortController?.abort(); }
    setPriceSortStatus(state, url) {
      this.dataset.priceSortState = state;
      for (const toggle of this.querySelectorAll('[aria-controls="facets-drawer"]')) toggle.disabled = state === 'pending';
      for (const sidebar of this.querySelectorAll('.facets-sidebar')) sidebar.toggleAttribute('inert', state === 'pending');
      const status = this.querySelector('[data-price-sort-status]');
      if (!status) return;
      status.hidden = state === 'ready' || state === 'native';
      status.querySelector('[data-price-sort-loading]')?.toggleAttribute('hidden', state !== 'pending');
      status.querySelector('[data-price-sort-error]')?.toggleAttribute('hidden', state !== 'error');
      const retry = status.querySelector('[data-price-sort-retry]');
      retry?.toggleAttribute('hidden', state !== 'error');
      if (retry && url) {
        const target = new URL(url, win.location.href);
        target.searchParams.delete('section_id'); target.searchParams.delete('sections');
        retry.href = target.toString();
      }
    }
    async initializePriceSort() {
      this.priceSortController?.abort();
      const controller = new win.AbortController(), url = new URL(win.location.href);
      this.priceSortController = controller;
      const current = () => this.isConnected && this.priceSortController === controller && !controller.signal.aborted;
      this.setPriceSortStatus('pending', url);
      try {
        // Native tabs may already own a consumed declarative shadow template.
        // Keep those connected components and their selected/focus state. The
        // guarded preparation commits only product cards after all reads pass.
        await preparePriceSortedSection(this, {url, signal: controller.signal, isCurrent: current, expectedRoot: this, win});
        if (current()) this.setPriceSortStatus('ready');
      } catch { if (current()) this.setPriceSortStatus('error', url); }
    }
  }
  win.customElements.define('raith-search-results', RaithSearchResults);
}
if (typeof window !== 'undefined' && window.customElements) registerRaithSearchResults(window);

